import {ThemeProvider} from '@emotion/react';
import {getBrandBase} from 'microsites/core/brand';
import {createContext, useContext} from 'react';
import type {MicrositeFeatureFlags} from './featureFlags';
import type {LanguageId} from '../translations/utils/languageManifest';
import type {BrandLogoResource, MicrositeImageResource} from '../types/imageResources';
import type {BrandBase} from '@root/brands/BrandBase';
import type {MicrositeTheme} from '@root/brands/MicrositeTheme';
import type {BrandId} from 'microsites/core/brand';
import type {ReactNode} from 'react';

export type ImageHooks = {
  readonly useMicrositeImage: (imageId: string) => MicrositeImageResource;
  readonly useFauxInstagramImage: (imageId: string) => MicrositeImageResource;
  readonly useBrandLogoImage: () => BrandLogoResource;
};

export type MicrositeRuntimeConfig = ImageHooks & {
  readonly brand: BrandBase;
  readonly micrositeTheme: MicrositeTheme;
  readonly locale: LanguageId;
  readonly featureFlags: MicrositeFeatureFlags;
};

const MicrositeRuntimeContext = createContext<MicrositeRuntimeConfig | null>(null);

type MicrositeRuntimeProviderProps = {
  readonly brandId: BrandId;
  readonly imageHooks: ImageHooks;
  readonly locale: LanguageId;
  readonly featureFlags: MicrositeFeatureFlags;
  readonly children: ReactNode;
};

export const MicrositeRuntimeProvider = ({brandId, imageHooks, locale, featureFlags, children}: MicrositeRuntimeProviderProps) => {
  const brand = getBrandBase(brandId);
  const micrositeTheme = brand.micrositeTheme;
  if (!micrositeTheme) {
    throw new Error(`Microsite theme missing for brand "${brandId}".`);
  }
  if (!locale) {
    throw new Error(`Microsite locale is required.`);
  }
  const value: MicrositeRuntimeConfig = {
    brand,
    micrositeTheme,
    locale,
    featureFlags,
    ...imageHooks,
  };

  return (
    <MicrositeRuntimeContext.Provider value={value}>
      <ThemeProvider theme={micrositeTheme}>{children}</ThemeProvider>
    </MicrositeRuntimeContext.Provider>
  );
};

const useMicrositeRuntime = () => {
  const value = useContext(MicrositeRuntimeContext);

  if (!value) {
    throw new Error(`Microsite runtime context missing. Wrap pages with MicrositeRuntimeProvider.`);
  }

  return value;
};

export const useMicrositeTranslationContext = () => {
  const {locale} = useMicrositeRuntime();
  return {locale};
};
export const useMicrositeBrand = () => useMicrositeRuntime().brand;
export const useMicrositeTheme = () => useMicrositeRuntime().micrositeTheme;
export const useMicrositeBrandLogoImage = () => useMicrositeRuntime().useBrandLogoImage();
export const useSharedMicrositeImage = (imageId: string) => useMicrositeRuntime().useMicrositeImage(imageId);
export const useSharedFauxInstagramImage = (imageId: string) => useMicrositeRuntime().useFauxInstagramImage(imageId);
export const useMicrositeFeatureFlags = () => useMicrositeRuntime().featureFlags;
